import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningFilled } from '@mint-lib/icons';
import { useQuery } from '@mint-lib/query-client';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Skeleton,
  typographyVariants,
} from '@mint-lib/tw-components';
import { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import {
  useFolderAccessDraft,
  type FolderAccessState,
} from '../../../hooks/knowledge-base/useFolderAccessDraft.js';
import { useFolderAccessUsers } from '../../../hooks/knowledge-base/useFolderAccessUsers.js';
import { useFolderMutations } from '../../../hooks/knowledge-base/useFolderMutations.js';
import { getFolderOption } from '../../../queries/knowledgeBase.js';
import { FolderAccessFields } from './FolderAccessFields.jsx';

type Props = {
  folderId: string;
  folderName: string;
  onClose: () => void;
};

type FooterProps = {
  canSave: boolean;
  onSave?: () => void;
  onClose: () => void;
};

type FormProps = {
  folder: KnowledgeBase.Folder;
  isSaving: boolean;
  onSave: (request: KnowledgeBase.SetFolderGrantsRequest) => void;
  onClose: () => void;
};

const toAccessState = (folder: KnowledgeBase.Folder): FolderAccessState => ({
  access: folder.allWorkspace ? 'workspace' : 'invited',
  ids: (folder.grantedUsers ?? []).flatMap(({ userId }) =>
    userId == null ? [] : [String(userId)],
  ),
});

const Message = ({ children }: { children: string }) => (
  <Alert severity="warning" variant="multiline">
    <WarningFilled />
    <p className={typographyVariants({ variant: 'bodyShort02' })}>{children}</p>
  </Alert>
);

const ShareFooter = ({ canSave, onSave, onClose }: FooterProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <DialogFooter className="flex flex-row items-center justify-end gap-2">
      <Button variant="plain" size="large" onClick={onClose}>
        {t('Cancel')}
      </Button>
      <Button
        size="large"
        disabled={!canSave}
        data-dd-action-name="Knowledge base: share folder confirm"
        onClick={onSave}
      >
        {t('Share folder')}
      </Button>
    </DialogFooter>
  );
};

const ShareFolderForm = ({ folder, isSaving, onSave, onClose }: FormProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const hasUnknownUsers = (folder.grantedUsers ?? []).some(
    ({ userId }) => userId == null,
  );

  const users = useFolderAccessUsers({
    granted: folder.grantedUsers,
    owner: folder.owner,
  });
  const draft = useFolderAccessDraft({
    initial: toAccessState(folder),
    meId: users.me.id,
  });

  // Unlike creating, an edit may take everyone else's access away.
  const canSave =
    Boolean(users.me.id) && !hasUnknownUsers && draft.isDirty && !isSaving;

  return (
    <>
      <DialogContent className="flex flex-col gap-6">
        {hasUnknownUsers && (
          <Message>
            {t(
              "Some users with access can't be shown. Access can't be edited right now.",
            )}
          </Message>
        )}

        <FolderAccessFields draft={draft} users={users} />
      </DialogContent>

      <ShareFooter
        canSave={canSave}
        onSave={() => onSave(draft.toRequest())}
        onClose={onClose}
      />
    </>
  );
};

export const ShareFolderDialog = ({ folderId, folderName, onClose }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { updateFolderAccess, isPending: isSaving } = useFolderMutations();

  const { data, isFetchedAfterMount, isFetching, isError } = useQuery({
    ...getFolderOption(folderId),
    refetchOnMount: 'always',
  });

  const [folder, setFolder] = useState<KnowledgeBase.Folder>();

  if (!folder && data && isFetchedAfterMount && !isFetching && !isError) {
    setFolder(data);
  }

  // A save in flight closes the dialog itself once it lands.
  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSave = async (request: KnowledgeBase.SetFolderGrantsRequest) => {
    if (await updateFolderAccess(folderId, request)) onClose();
  };

  return (
    <Dialog modal open onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot aria-describedby={undefined} size="s" className="w-130">
          <DialogHeader>
            <DialogTitle>
              {t('Share {{name}}', { name: folderName })}
            </DialogTitle>
          </DialogHeader>

          {folder ? (
            <ShareFolderForm
              folder={folder}
              isSaving={isSaving}
              onSave={(request) => void handleSave(request)}
              onClose={handleClose}
            />
          ) : (
            <>
              <DialogContent className="flex flex-col gap-4">
                {isError ? (
                  <Message>
                    {t('Something went wrong. Access could not be loaded')}
                  </Message>
                ) : (
                  <div
                    className="flex flex-col gap-4"
                    data-testid="share-folder-loading"
                  >
                    <Skeleton variant="rounded" className="h-10" />
                    <Skeleton variant="rounded" className="h-10" />
                    <Skeleton variant="rounded" className="h-10" />
                  </div>
                )}
              </DialogContent>

              <ShareFooter canSave={false} onClose={handleClose} />
            </>
          )}
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
